import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import { SENEGAL_PHOTOS } from '../../shared/assets/categoryImages';

/**
 * Senegal-only landing hero. Replaces the European editorial collage with
 * locally-shot photos of real trades (plumber, photographer, electrician,
 * housekeeper) and a copy focused on the practical service-marketplace
 * value proposition: verified providers, nationwide coverage, direct
 * contact. Composed of:
 *
 *  • Left column — typographic block with overline, headline, subtitle,
 *    three trust chips and a single CTA towards the search page.
 *  • Right column (desktop) — four-photo collage anchored by the plumber
 *    shot; tilted, layered cards mirror the European hero's editorial
 *    rhythm so the home page still feels part of the same product.
 *  • Mobile (xs/sm) — the desktop collage collapses into a horizontally
 *    scrollable strip below the CTA, snap-aligned per card.
 */
export function SenegalHero() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Four representative shots covering the three category groups so the
  // collage previews the full Senegal taxonomy at a glance.
  const photos = [
    { src: SENEGAL_PHOTOS.plumber, label: 'plumber' },
    { src: SENEGAL_PHOTOS.photographer, label: 'photographer' },
    { src: SENEGAL_PHOTOS.houseCleaning, label: 'houseCleaning' },
    { src: SENEGAL_PHOTOS.mason, label: 'mason' },
  ].filter((p) => p.src);

  const trustPills: { icon: React.ReactElement; key: string }[] = [
    { icon: <VerifiedRoundedIcon fontSize="small" />, key: 'senegalHome.trustVerified' },
    { icon: <PlaceRoundedIcon fontSize="small" />, key: 'senegalHome.trustNationwide' },
    { icon: <ChatRoundedIcon fontSize="small" />, key: 'senegalHome.trustDirect' },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, md: 9 },
        // Same warm radial gradient as the European hero so the
        // brand identity stays consistent across markets.
        backgroundImage: (theme) =>
          theme.palette.mode === 'dark'
            ? `radial-gradient(60% 80% at 20% 0%, ${theme.palette.primary.dark}55 0%, transparent 60%),
               radial-gradient(50% 80% at 90% 30%, ${theme.palette.secondary.dark}40 0%, transparent 60%)`
            : `radial-gradient(60% 80% at 20% 0%, ${theme.palette.primary.light}33 0%, transparent 60%),
               radial-gradient(50% 80% at 90% 30%, ${theme.palette.secondary.light}26 0%, transparent 60%)`,
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* LEFT — typography + trust + CTA */}
          <Grid item xs={12} md={7}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                letterSpacing: '0.18em',
                fontWeight: 700,
                mb: 2,
                display: 'inline-block',
              }}
            >
              {t('senegalHome.heroOverline')}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                mb: 2.5,
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('senegalHome.heroTitle')}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ fontSize: { xs: '1.0625rem', md: '1.25rem' }, lineHeight: 1.55, mb: 3 }}
            >
              {t('senegalHome.heroSubtitle')}
            </Typography>

            {/* Trust chip row — three pillars that matter for trade
                services: verification, national coverage, direct contact. */}
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 4 }}
            >
              {trustPills.map((p) => (
                <Chip
                  key={p.key}
                  icon={p.icon}
                  label={t(p.key)}
                  sx={{
                    fontWeight: 600,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    color: 'text.primary',
                    px: 0.5,
                    '& .MuiChip-icon': { color: 'primary.main' },
                  }}
                />
              ))}
            </Stack>

            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => navigate('/search')}
              sx={{ px: 4, py: 1.5 }}
            >
              {t('senegalHome.heroCta')}
            </Button>

            {/* Mobile strip — same four shots, scrollable. Edge bleed for
                an immersive feel; scrollbar hidden so the row reads as a
                story, not a UI control. */}
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                mt: 4,
                mx: -2,
                px: 2,
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                scrollSnapType: 'x mandatory',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              <Stack direction="row" spacing={1.5} sx={{ width: 'max-content', py: 1 }}>
                {photos.map((p, i) => (
                  <Box key={p.label} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                    <Box
                      component="img"
                      src={p.src}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: { xs: 140, sm: 170 },
                        height: { xs: 180, sm: 210 },
                        objectFit: 'cover',
                        borderRadius: 3,
                        transform:
                          i % 2 === 0 ? 'rotate(-2deg)' : 'translateY(-6px) rotate(2deg)',
                        boxShadow: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '0 12px 28px rgba(0,0,0,0.55)'
                            : '0 12px 28px rgba(26,24,22,0.16)',
                        border: '3px solid',
                        borderColor: 'background.default',
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* RIGHT — desktop collage. Asymmetric, layered shots mirror the
              editorial layout used for the European market so the brand
              feel is consistent across markets. */}
          <Grid item md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'relative', height: 500, width: '100%' }}>
              {/* Plomber — hero anchor, biggest card, top-right. */}
              {photos[0] && (
                <Box
                  component="img"
                  src={photos[0].src}
                  alt=""
                  loading="eager"
                  decoding="async"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 24,
                    width: 230,
                    height: 290,
                    objectFit: 'cover',
                    borderRadius: 4,
                    boxShadow: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '0 24px 60px rgba(0,0,0,0.6)'
                        : '0 24px 60px rgba(26,24,22,0.18)',
                    transform: 'rotate(3deg)',
                  }}
                />
              )}
              {/* Photographer — bottom-left anchor, square. */}
              {photos[1] && (
                <Box
                  component="img"
                  src={photos[1].src}
                  alt=""
                  loading="eager"
                  decoding="async"
                  sx={{
                    position: 'absolute',
                    bottom: 24,
                    left: 0,
                    width: 195,
                    height: 195,
                    objectFit: 'cover',
                    borderRadius: 4,
                    boxShadow: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '0 20px 50px rgba(0,0,0,0.55)'
                        : '0 20px 50px rgba(26,24,22,0.16)',
                    transform: 'rotate(-5deg)',
                    border: '4px solid',
                    borderColor: 'background.default',
                  }}
                />
              )}
              {/* Cleaning — bottom-right anchor. */}
              {photos[2] && (
                <Box
                  component="img"
                  src={photos[2].src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 165,
                    height: 210,
                    objectFit: 'cover',
                    borderRadius: 4,
                    boxShadow: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '0 20px 50px rgba(0,0,0,0.55)'
                        : '0 20px 50px rgba(26,24,22,0.16)',
                    transform: 'rotate(6deg)',
                    border: '4px solid',
                    borderColor: 'background.default',
                  }}
                />
              )}
              {/* Mason — top-left, smaller accent. */}
              {photos[3] && (
                <Box
                  component="img"
                  src={photos[3].src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  sx={{
                    position: 'absolute',
                    top: 36,
                    left: 8,
                    width: 140,
                    height: 175,
                    objectFit: 'cover',
                    borderRadius: 3,
                    boxShadow: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '0 16px 40px rgba(0,0,0,0.5)'
                        : '0 16px 40px rgba(26,24,22,0.14)',
                    transform: 'rotate(-4deg)',
                    border: '4px solid',
                    borderColor: 'background.default',
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
